import type { Entry, Pricing } from '../types';
import { hasIcon } from '../ui/icon';
import { CATEGORY_META, metaFor } from './categories';
import data from './products.json';

/**
 * Ben's own apps and services, listed by hand in products.json and shown in the
 * directory beside the factory builds under the Products type. Unlike a factory
 * entry, a product never carries analytics (attachStats skips them, and the
 * nightly analytics file never holds their hosts), and its row says how it is
 * paid for and where it runs. Its date is the day it shipped (App Store release,
 * or the day the service went live); a product still coming soon carries the day
 * work on it began, which orders the list while the row says "Coming soon".
 */
export interface RawProduct {
  name: string;
  slug: string;
  description: string;
  url: string | null;
  linkLabel?: string;
  icon: string;
  category: string;
  country: string | null;
  tags: string[];
  date: string;
  pricing: string;
  platforms: string[];
  comingSoon: boolean;
}

export const PRICING_LABEL: Record<Pricing, string> = {
  free: 'Free',
  freemium: 'Freemium',
  paid: 'Paid',
};

/** Where a product runs: the owner's Mobile, Web and API, and Desktop for Captive Portal (20 Oct 2026). */
export const PLATFORMS = ['Mobile', 'Web', 'API', 'Desktop'] as const;

function isPricing(value: string): value is Pricing {
  return Object.prototype.hasOwnProperty.call(PRICING_LABEL, value);
}

/** A product as a directory entry. Throws on a malformed one, so a bad edit to
 *  products.json fails the tests and the build rather than showing a half row. */
export function productEntry(raw: RawProduct): Entry {
  const problem = (what: string) => new Error(`products.json: ${raw.slug || raw.name}: ${what}`);
  if (!raw.name || !raw.slug) throw problem('needs a name and a slug');
  if (!isPricing(raw.pricing)) throw problem(`pricing must be free, freemium or paid, not "${raw.pricing}"`);
  if (raw.platforms.length === 0) throw problem('needs at least one platform');
  for (const p of raw.platforms) {
    if (!(PLATFORMS as readonly string[]).includes(p)) throw problem(`unknown platform "${p}"`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw.date)) throw problem(`date must be YYYY-MM-DD, not "${raw.date}"`);
  if (!CATEGORY_META[raw.category]) throw problem(`unknown category "${raw.category}"`);
  if (raw.url !== null && !raw.url.startsWith('https://')) throw problem('url must be https');
  if (!raw.comingSoon && !raw.url) throw problem('a shipped product needs a url');
  return {
    name: raw.name,
    slug: raw.slug,
    description: raw.description,
    url: raw.url,
    // No repo: products are listed by hand, so no push time joins on and no
    // NEW or UPDATED badge lights from a repo.
    repo: null,
    tags: raw.tags,
    type: 'product',
    date: raw.date,
    icon: hasIcon(raw.icon) ? raw.icon : metaFor(raw.category).icon,
    category: raw.category,
    country: raw.country,
    host: raw.url ? new URL(raw.url).host : null,
    stats: null,
    product: {
      pricing: raw.pricing,
      platforms: raw.platforms,
      comingSoon: raw.comingSoon,
      ...(raw.linkLabel ? { linkLabel: raw.linkLabel } : {}),
    },
  };
}

export const PRODUCTS: Entry[] = (data.products as RawProduct[]).map(productEntry);

/** The factory entries with Ben's products added. Applied after the factory
 *  blocklist (Provenova's factory entry stays hidden; its product entry is shown). */
export function withProducts(entries: Entry[]): Entry[] {
  return [...entries, ...PRODUCTS];
}

/** Heads the Products view: these aren't the factories' work. */
export const PRODUCTS_NOTE = {
  title: "Ben's personal product portfolio.",
  body: 'Carefully curated, iterated, customer-focussed apps and sites.',
};

/** In a product's expanded row (the owner's words, 20 Oct 2026). */
export const PRODUCT_ROW_NOTE =
  'This is a complex, handcrafted, customer-focussed product (not a simple website generated in the daily generation experiment).';

/** How a product is paid for, then where it runs: ['Freemium', 'Mobile']. */
export function productPills(entry: Entry): { price: string; platforms: string[] } | null {
  if (!entry.product) return null;
  return { price: PRICING_LABEL[entry.product.pricing], platforms: entry.product.platforms };
}

/** "Freemium · Web, API": how a product is paid for and where it runs. */
export function productSummary(entry: Entry): string {
  if (!entry.product) return '';
  return `${PRICING_LABEL[entry.product.pricing]} · ${entry.product.platforms.join(', ')}`;
}
